import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  FiSave,
  FiTrash2,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiMic,
  FiMicOff,
  FiXCircle,
  FiFacebook, // Thêm icon cho Facebook
  FiAlertTriangle, // Thêm icon cảnh báo
  FiChevronDown,
  FiChevronUp,
  FiInfo, // Thêm icon mới
} from "react-icons/fi"; // Thêm các icon mới
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  getDatabase,
  ref,
  get,
  update,
  push,
  remove,
  set,
  onValue,
  child,
} from "firebase/database";
import CustomTextArea from "../components/CustomTextArea";
import {
  POST_LENGTHS,
  POST_STYLES,
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
} from "../constants/postSettings";
import { GoogleGenerativeAI, SchemaType } from "@google/generative-ai";
import { PublishingPlatforms } from "../constants/sharedTypes";
import { useDomainConfig } from "../hooks/useDomainConfig";
import { getDomainConfig } from "../constants/domains";
import BasicInfoSection from "../components/BasicInfoSection";
import ProjectInfoSection from "../components/ProjectInfoSection";
import { app } from "../firebase/config";

// Import các hàm logic
import {
  generateFacebookTopics,
  deleteTopic,
  resetTopicsStatus,
  saveAutoPostSettings,
  capitalizeFirstLetter,
} from "./utils/autoPostGeneratorLogic";
import { getTotalActivePostingHours } from "./utils/autoPostLimitUtils";

// Import các hàm Firebase
import { deleteConfigFromFirebase } from "./utils/firebaseUtils";
import CustomTimePicker from "../NormalPostGenerator/components/CustomTimePicker";
import axios from "axios";

/**
 * AutoPostGenerator Component
 *
 * 🆓 FREE TIER: This component is now completely free to use.
 * - No subscription checks
 * - No point deduction
 * - Unlimited auto-post configurations
 * - Payment system disabled but code kept for future re-enabling
 */
const AutoPostGenerator = () => {
  const [userMembership, setUserMembership] = useState(null); // Kept for backward compatibility
  const location = useLocation();
  const config = location.state?.config || {};

  // Thêm ref để track component mount status
  const isMountedRef = useRef(true);

  // ✅ FIXED: Domain configuration hook - updated like NormalPostGenerator
  const { domainConfig, switchDomain, refreshKey, isLoading, selectedDomain } =
    useDomainConfig();

  // ✅ FIXED: Track whether saved data has been loaded
  const [hasSavedDataLoaded, setHasSavedDataLoaded] = useState(false);

  // Dữ liệu cho các dropdown
  // Không cần định nghĩa lại do đã import từ file constants/realEstateTypes.js

  // State cho các dropdown mới
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [selectedContentType, setSelectedContentType] = useState("");
  const [selectedPublishingPlatform, setSelectedPublishingPlatform] =
    useState("");

  // State cho dropdown refs
  const projectTypeDropdownRef = useRef(null);
  const contentTypeDropdownRef = useRef(null);
  const publishingPlatformDropdownRef = useRef(null);

  // State cho dropdown visibility
  const [isProjectTypeDropdownOpen, setIsProjectTypeDropdownOpen] =
    useState(false);
  const [isContentTypeDropdownOpen, setIsContentTypeDropdownOpen] =
    useState(false);
  const [
    isPublishingPlatformDropdownOpen,
    setIsPublishingPlatformDropdownOpen,
  ] = useState(false);

  // State cho thông tin khách hàng mục tiêu
  const [targetCustomer, setTargetCustomer] = useState("");

  // Thêm state cho URL tham khảo
  const [referenceUrl, setReferenceUrl] = useState("");

  // State for accordion sections
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    projectInfo: true,
    customization: true,
  });

  const [requirements, setRequirements] = useState(config.prompt || "");
  // Set default post length based on platform
  const [postLength, setPostLength] = useState(
    config.postLength || DEFAULT_POST_LENGTH
  );
  const [postStyles, setPostStyles] = useState(
    config.postStyles ? config.postStyles.split(", ") : DEFAULT_POST_STYLES
  );
  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState(
    config.fanpageID ? [{ id: config.fanpageID, name: config.fanpageName }] : []
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasFanpages, setHasFanpages] = useState(true); // Thêm state để kiểm tra có fanpage hay không
  // Thêm thời gian mặc định 8h sáng nếu không có thời gian nào đã chọn
  const [selectedTimes, setSelectedTimes] = useState(
    config.times && config.times.length > 0 ? config.times : ["08:00"]
  );
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [inputType, setInputType] = useState("topic"); // Mặc định là tạo từ chủ đề
  const [topics, setTopics] = useState({}); // Changed from array to object/dictionary
  const [isGeneratingTopics, setIsGeneratingTopics] = useState(false); // New state for loading effect
  // const [products, setProducts] = useState([]); // Add this new state
  const [topicCount, setTopicCount] = useState(30); // Add this new state
  const [isTopicCountDropdownOpen, setIsTopicCountDropdownOpen] =
    useState(false); // Thêm state mới
  const topicCountDropdownRef = useRef(null); // Thêm ref mới
  const [includeContactInfo, setIncludeContactInfo] = useState(
    config.includeContactInfo || false
  );
  const [useFanpageInfo, setUseFanpageInfo] = useState(
    config.useFanpageInfo || false
  );
  const [allowNoImage, setAllowNoImage] = useState(
    config.allowNoImage || false
  );
  const [platformType, setPlatformType] = useState("facebook"); // Default to facebook
  const [autoPostEnabled, setAutoPostEnabled] = useState(
    config.autoPostEnabled || true
  ); // Add autoPostEnabled state

  // Mặc định nền tảng là Facebook
  const [platform, setPlatform] = useState("facebook");

  // Thêm state để lưu settings
  const [settings, setSettings] = useState({
    postLength: config.postLength || POST_LENGTHS[0].value, // "Ngắn gọn" for Facebook
    postStyles: config.postStyles
      ? config.postStyles.split(", ")
      : DEFAULT_POST_STYLES,
    includeContactInfo: config.includeContactInfo || false,
    useFanpageInfo: config.useFanpageInfo || false,
    autoPostEnabled: config.autoPostEnabled || false,
    inputType: config.inputType || "topic",
    topicCount: 30,
    allowNoImage: config.allowNoImage || false,
    platformType: config.platformType || "facebook",
    platform: config.platform || "facebook",
  });

  // Thêm state cho selectedDays
  const [selectedDays, setSelectedDays] = useState(
    config.selectedDays || {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    }
  );

  // Thêm daysInVietnamese object
  const daysInVietnamese = {
    monday: "Thứ 2",
    tuesday: "Thứ 3",
    wednesday: "Thứ 4",
    thursday: "Thứ 5",
    friday: "Thứ 6",
    saturday: "Thứ 7",
    sunday: "Chủ nhật",
  };

  const [isAnalyzingRequest, setIsAnalyzingRequest] = useState(false); // Thêm state mới cho trạng thái phân tích

  // Thêm các state mới cho chức năng ghi âm
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);

  // Thêm state mới để lưu transcript tạm thời
  const [tempTranscript, setTempTranscript] = useState("");

  // Thêm state để theo dõi navigation
  const [isNavigating, setIsNavigating] = useState(false);

  // Thêm state cho tài liệu tham khảo
  const [contextFiles, setContextFiles] = useState([]);
  const [isProcessingContext, setIsProcessingContext] = useState(false);

  // State for drag and drop
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  // State for auto post hours limit
  const [hourLimitInfo, setHourLimitInfo] = useState({
    totalHours: 0,
    maxHours: 5,
    remaining: 5,
  });

  // Khởi tạo Google Generative AI client
  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

  // Refs cho tài liệu tham khảo
  const contextFileInputRef = useRef(null);

  // Đảm bảo postLength luôn có giá trị valid
  useEffect(() => {
    if (!postLength || !POST_LENGTHS.find((p) => p.value === postLength)) {
      console.log(
        "🔧 AutoPostGenerator: Setting fallback postLength to DEFAULT_POST_LENGTH"
      );
      setPostLength(DEFAULT_POST_LENGTH);
    }
  }, [postLength]);

  // Debug postLength changes
  useEffect(() => {
    console.log("🔍 AutoPostGenerator postLength changed:", {
      postLength,
      isValid: POST_LENGTHS.find((p) => p.value === postLength),
      DEFAULT_POST_LENGTH,
      POST_LENGTHS: POST_LENGTHS.map((p) => p.value),
    });
  }, [postLength]);

  // Initialize state with domain defaults when domain config changes
  useEffect(() => {
    if (domainConfig && domainConfig.defaults && !isLoading) {
      console.log(
        "🔄 AutoPostGenerator: Setting initial defaults from domainConfig:",
        {
          domainId: domainConfig.id,
          domainName: domainConfig.name,
          selectedDomain,
          configMatchesDomain: domainConfig.id === selectedDomain,
          defaults: domainConfig.defaults,
          currentProjectType: selectedProjectType,
          currentContentType: selectedContentType,
          currentPostLength: postLength,
          currentPostStyles: postStyles,
          hasSavedDataLoaded: hasSavedDataLoaded,
        }
      );

      // ✅ FIXED: Only proceed if domainConfig matches the currently selected domain
      if (domainConfig.id !== selectedDomain) {
        console.log(
          `⚠️ AutoPostGenerator: Config mismatch - domainConfig.id: ${domainConfig.id}, selectedDomain: ${selectedDomain}. Waiting for sync...`
        );
        return;
      }

      // ✅ FIXED: Only set defaults if no saved data was loaded
      if (!hasSavedDataLoaded) {
        // ✅ FIXED: Set defaults if current values are empty OR if they are real-estate defaults but we're in different domain
        const isCurrentlyRealEstateDefaults =
          selectedProjectType === "Dự án khu đô thị (hỗn hợp)" &&
          selectedContentType === "Giới thiệu, cập nhật thông tin dự án";

        const shouldSetDefaults =
          !selectedProjectType ||
          !selectedContentType ||
          (isCurrentlyRealEstateDefaults && domainConfig.id !== "real-estate");

        if (shouldSetDefaults || !selectedProjectType) {
          setSelectedProjectType(domainConfig.defaults.projectType);
          console.log(
            "✅ Set default project type:",
            domainConfig.defaults.projectType
          );
        }

        if (shouldSetDefaults || !selectedContentType) {
          setSelectedContentType(domainConfig.defaults.contentType);
          console.log(
            "✅ Set default content type:",
            domainConfig.defaults.contentType
          );
        }

        if (
          shouldSetDefaults ||
          (postLength === DEFAULT_POST_LENGTH &&
            domainConfig.defaults.postLength)
        ) {
          setPostLength(domainConfig.defaults.postLength);
          console.log(
            "✅ Set default post length:",
            domainConfig.defaults.postLength
          );
        }

        if (
          shouldSetDefaults ||
          (postStyles.length === 1 &&
            postStyles[0] === DEFAULT_POST_STYLES[0] &&
            domainConfig.defaults.postStyles)
        ) {
          setPostStyles(domainConfig.defaults.postStyles);
          console.log(
            "✅ Set default post styles:",
            domainConfig.defaults.postStyles
          );
        }
      } else {
        console.log(
          "⏭️ AutoPostGenerator: Skipping domain defaults because saved data was loaded"
        );
      }
    } else {
      console.log(
        "⏳ AutoPostGenerator: Waiting for domainConfig to be ready...",
        {
          hasDomainConfig: !!domainConfig,
          hasDefaults: !!(domainConfig && domainConfig.defaults),
          isLoading,
          hasSavedDataLoaded: hasSavedDataLoaded,
        }
      );
    }
  }, [
    domainConfig,
    isLoading,
    hasSavedDataLoaded,
    refreshKey, // ✅ Add refreshKey to ensure re-run when domain changes
    selectedDomain, // ✅ Add selectedDomain to track domain changes
    // ✅ FIXED: Remove current state from dependencies to avoid infinite loop
    // selectedProjectType,
    // selectedContentType,
    // postLength,
    // postStyles,
  ]);

  // ✅ FIXED: Log để debug domainConfig changes - chỉ log khi cần thiết
  useEffect(() => {
    if (domainConfig && !isLoading) {
      console.log("🔍 AutoPostGenerator - Domain Config Changed:", {
        domainConfig: domainConfig.name,
        refreshKey,
        isLoading,
        selectedDomain,
        projectTypesCount: Object.keys(domainConfig?.projectTypes || {}).length,
        contentTypesCount: Object.keys(domainConfig?.contentTypes || {}).length,
      });
    }
  }, [domainConfig?.id, refreshKey, isLoading, selectedDomain]);

  // Handle domain change - reset form to new domain defaults
  const handleDomainChange = (newDomainId) => {
    console.log(
      `🔄 AutoPostGenerator: Handling domain change to: ${newDomainId}`
    );

    // ✅ FIXED: Sử dụng switchDomain từ hook thay vì gọi getDomainConfig trực tiếp
    switchDomain(newDomainId);

    // ✅ FIXED: Lấy config mới sau khi switch
    const newDomainConfig = getDomainConfig(newDomainId);

    if (newDomainConfig && newDomainConfig.defaults) {
      console.log(`✅ New domain config loaded:`, {
        domain: newDomainConfig.name,
        defaults: newDomainConfig.defaults,
        projectTypesCount: Object.keys(newDomainConfig.projectTypes || {})
          .length,
        contentTypesCount: Object.keys(newDomainConfig.contentTypes || {})
          .length,
      });

      // ✅ FIXED: Sử dụng setTimeout để đảm bảo state update sau khi domain đã switch
      setTimeout(() => {
        // Reset form fields to new domain defaults
        setSelectedProjectType(newDomainConfig.defaults.projectType);
        setSelectedContentType(newDomainConfig.defaults.contentType);
        setPostLength(
          newDomainConfig.defaults.postLength || DEFAULT_POST_LENGTH
        );
        setPostStyles(
          newDomainConfig.defaults.postStyles || DEFAULT_POST_STYLES
        );

        // Clear user inputs
        setRequirements("");
        setTargetCustomer("");
        setReferenceUrl("");

        // Clear topics
        setTopics({});

        // Reset settings về default
        setAllowNoImage(false);
        setIncludeContactInfo(false);
        setUseFanpageInfo(false);
        setTopicCount(30);

        // Close all dropdowns
        setIsProjectTypeDropdownOpen(false);
        setIsContentTypeDropdownOpen(false);
        setIsPublishingPlatformDropdownOpen(false);

        console.log(
          `✅ Form reset completed for domain: ${newDomainConfig.name}`
        );
      }, 50); // ✅ Small delay để đảm bảo domain config đã được update

      console.log(
        `✅ Domain change completed successfully to: ${newDomainConfig.name}`
      );
    } else {
      console.error(`❌ Failed to load config for domain: ${newDomainId}`);
      toast.error("Có lỗi khi chuyển lĩnh vực hoạt động. Vui lòng thử lại.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Lấy thông tin membership của user
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const db = getDatabase();
    const membershipRef = ref(db, `/users/${userId}/membership`);

    const unsubscribe = onValue(membershipRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserMembership(snapshot.val());
      }
    });

    // Cleanup listener khi component unmount để tránh memory leak
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadFacebookData = async () => {
      // Kiểm tra xem người dùng đã đăng nhập chưa bằng cách kiểm tra userId
      const userId = localStorage.getItem("userId");
      if (!userId) {
        return; // Không navigate, để App.js xử lý authentication
      }

      try {
        // userId đã được định nghĩa ở trên

        const db = getDatabase();
        const connectedFanpagesRef = ref(
          db,
          `users/${userId}/connectedFanpages`
        );
        const connectedFanpagesSnapshot = await get(connectedFanpagesRef);

        if (connectedFanpagesSnapshot.exists()) {
          const connectedData = connectedFanpagesSnapshot.val();
          const fanpageList = Object.values(connectedData);

          console.log("Fanpages loaded from Firebase:", fanpageList);

          if (fanpageList.length > 0) {
            setFanpages(fanpageList);
            setHasFanpages(true);

            // Nếu chưa có fanpage được chọn và có ít nhất 1 fanpage
            if (selectedFanpages.length === 0) {
              // Nếu chỉ có 1 fanpage, tự động chọn fanpage đó
              if (fanpageList.length === 1) {
                setSelectedFanpages([fanpageList[0]]);
              }
              // Nếu có nhiều fanpage và có fanpage ID trong config, chọn fanpage đó
              else if (config.fanpageID) {
                const selectedFanpage = fanpageList.find(
                  (fanpage) => fanpage.id === config.fanpageID
                );
                if (selectedFanpage) {
                  setSelectedFanpages([selectedFanpage]);
                }
              }
            }
          } else {
            setHasFanpages(false);
            setFanpages([]);
            setSelectedFanpages([]);
          }
        } else {
          console.log("Không tìm thấy fanpage nào trong Firebase");
          setHasFanpages(false);
          setFanpages([]);
          setSelectedFanpages([]);
        }
      } catch (error) {
        console.error("Error fetching fanpages from Firebase:", error);
        setHasFanpages(false);
        setFanpages([]);
        setSelectedFanpages([]);
      }
    };

    // Chỉ load fanpages khi component mount, không phụ thuộc vào selectedFanpages.length
    loadFacebookData();
  }, [config.fanpageID]);

  // COMMENTED OUT: Loại bỏ global event listener để tránh can thiệp navigation
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     // Kiểm tra xem click có phải từ sidebar navigation hoặc link navigation không
  //     const sidebar = document.querySelector("nav");
  //     const links = event.target.closest("a, button[data-navigate]");

  //     if ((sidebar && sidebar.contains(event.target)) || links) {
  //       return; // Không xử lý nếu click vào sidebar hoặc navigation links
  //     }

  //     // Chỉ xử lý dropdown khi thực sự cần thiết
  //     if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
  //       setIsDropdownOpen(false);
  //     }
  //     if (
  //       topicCountDropdownRef.current &&
  //       !topicCountDropdownRef.current.contains(event.target)
  //     ) {
  //       setIsTopicCountDropdownOpen(false);
  //     }

  //     // Xử lý click outside cho các dropdown mới
  //     if (
  //       projectTypeDropdownRef.current &&
  //       !projectTypeDropdownRef.current.contains(event.target)
  //     ) {
  //       setIsProjectTypeDropdownOpen(false);
  //     }
  //     if (
  //       contentTypeDropdownRef.current &&
  //       !contentTypeDropdownRef.current.contains(event.target)
  //     ) {
  //       setIsContentTypeDropdownOpen(false);
  //     }
  //     if (
  //       publishingPlatformDropdownRef.current &&
  //       !publishingPlatformDropdownRef.current.contains(event.target)
  //     ) {
  //       setIsPublishingPlatformDropdownOpen(false);
  //     }
  //   };

  //   // Sử dụng capture để đảm bảo không can thiệp navigation
  //   document.addEventListener("mousedown", handleClickOutside, {
  //     capture: false,
  //   });
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside, {
  //       capture: false,
  //     });
  //   };
  // }, []);

  useEffect(() => {
    if (config.id) {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      const configRef = ref(db, `posts/${userId}/${config.id}`);

      get(configRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();

            // ✅ FIXED: Mark that saved data has been loaded
            setHasSavedDataLoaded(true);
            console.log("📦 AutoPostGenerator: Loaded saved config data");

            // Set default post length - đảm bảo luôn có giá trị hợp lệ
            const savedPostLength = data.postLength;
            if (
              savedPostLength &&
              POST_LENGTHS.find((p) => p.value === savedPostLength)
            ) {
              setPostLength(savedPostLength);
            } else {
              setPostLength(DEFAULT_POST_LENGTH);
            }

            // Chỉ lấy style đầu tiên nếu có nhiều style
            const stylesList = data.postStyles
              ? data.postStyles.split(", ")
              : DEFAULT_POST_STYLES;
            setPostStyles(
              stylesList.length > 0 ? [stylesList[0]] : [DEFAULT_POST_STYLES[0]]
            );

            // Convert single time to array format if needed
            setSelectedTimes(
              data.times ? data.times : [data.time] || ["00:00"]
            );
            setInputType(data.inputType || "topic");
            setAutoPostEnabled(data.autoPostEnabled || false);
            setIncludeContactInfo(data.includeContactInfo || false);
            setUseFanpageInfo(data.useFanpageInfo || false);
            setAllowNoImage(data.allowNoImage || false);
            setPlatform(data.platform || "facebook");

            // Cập nhật các trường mới
            if (data.projectType) setSelectedProjectType(data.projectType);
            if (data.contentType) setSelectedContentType(data.contentType);
            if (data.publishingPlatform)
              setSelectedPublishingPlatform(data.publishingPlatform);
            if (data.targetCustomer) setTargetCustomer(data.targetCustomer);
            if (data.referenceUrl) setReferenceUrl(data.referenceUrl);
            if (data.contextFiles) setContextFiles(data.contextFiles);

            // Cập nhật topics nếu có
            if (data.topics && Object.keys(data.topics).length > 0) {
              setTopics(data.topics);
            }

            // Cập nhật settings với postLength đã được validate
            const validPostLength =
              data.postLength &&
              POST_LENGTHS.find((p) => p.value === data.postLength)
                ? data.postLength
                : DEFAULT_POST_LENGTH;

            setSettings({
              postLength: validPostLength,
              postStyles: data.postStyles
                ? data.postStyles.split(", ")
                : DEFAULT_POST_STYLES,
              includeContactInfo: data.includeContactInfo || false,
              useFanpageInfo: data.useFanpageInfo || false,
              autoPostEnabled: data.autoPostEnabled || false,
              inputType: data.inputType || "topic",
              topicCount: data.topicCount || 30,
              allowNoImage: data.allowNoImage || false,
              platformType: data.platformType || "facebook",
              platform: data.platform || "facebook",
            });

            // Update platformType if it exists in the data
            if (data.platformType) {
              setPlatformType(data.platformType);
            }

            // Nếu có wordpressSiteId, khi tải dữ liệu wordpressSites sẽ tự động chọn site tương ứng
          } else {
            // ✅ FIXED: No saved data found, mark as not loaded
            console.log("🆕 AutoPostGenerator: No saved config data found");
            setHasSavedDataLoaded(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching config:", error);
          setHasSavedDataLoaded(false);
          toast.error("Không thể tải dữ liệu cấu hình!", {
            position: "top-right",
            autoClose: 3000,
          });
        });
    } else {
      // ✅ FIXED: No config.id means no saved data
      console.log(
        "🆕 AutoPostGenerator: No config.id, will use domain defaults"
      );
      setHasSavedDataLoaded(false);
    }
  }, [config.id]);

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const settingsRef = ref(
          db,
          `users/${userId}/settings/autoPostGenerator`
        );
        const snapshot = await get(settingsRef);

        if (snapshot.exists()) {
          const loadedSettings = snapshot.val();

          // ✅ FIXED: Mark that saved data has been loaded
          setHasSavedDataLoaded(true);
          console.log("📦 AutoPostGenerator: Loaded saved user settings");

          setSettings(loadedSettings);
          // Cập nhật các state riêng lẻ với validation
          // Validate và set postLength
          const savedPostLength = loadedSettings.postLength;
          if (
            savedPostLength &&
            POST_LENGTHS.find((p) => p.value === savedPostLength)
          ) {
            setPostLength(savedPostLength);
          } else {
            setPostLength(DEFAULT_POST_LENGTH);
          }

          // Đảm bảo chỉ lấy một style
          let styles = loadedSettings.postStyles || DEFAULT_POST_STYLES;
          if (Array.isArray(styles) && styles.length > 0) {
            setPostStyles([styles[0]]);
          } else if (typeof styles === "string" && styles) {
            setPostStyles([styles.split(", ")[0]]);
          } else {
            setPostStyles([DEFAULT_POST_STYLES[0]]);
          }

          setIncludeContactInfo(loadedSettings.includeContactInfo || false);
          setUseFanpageInfo(loadedSettings.useFanpageInfo || false);
          setAutoPostEnabled(loadedSettings.autoPostEnabled || false);
          setInputType(loadedSettings.inputType || "topic");
          setTopicCount(loadedSettings.topicCount || 30);
          setAllowNoImage(loadedSettings.allowNoImage || false);
          setPlatformType(loadedSettings.platformType || "facebook");
          setPlatform(loadedSettings.platform || "facebook");
          if (loadedSettings.referenceUrl)
            setReferenceUrl(loadedSettings.referenceUrl);
          if (loadedSettings.contextFiles)
            setContextFiles(loadedSettings.contextFiles);
        } else {
          // ✅ FIXED: No saved settings found, mark as not loaded
          console.log("🆕 AutoPostGenerator: No saved user settings found");
          setHasSavedDataLoaded(false);
        }
      } catch (error) {
        console.error("Error loading settings:", error);
        setHasSavedDataLoaded(false);
      }
    };

    if (!config) {
      loadUserSettings();
    }
  }, []);

  // Load thông tin giới hạn giờ đăng bài
  useEffect(() => {
    const loadHourLimit = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const info = await getTotalActivePostingHours(userId, config.id);
      setHourLimitInfo(info);
    };

    loadHourLimit();
  }, [config.id, autoPostEnabled, selectedTimes]);

  // Thêm hàm saveUserSettings với debounce để tránh gọi quá thường xuyên
  const saveUserSettings = useCallback(
    async (newSettings = null) => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const settingsRef = ref(
          db,
          `users/${userId}/settings/autoPostGenerator`
        );

        // Nếu có newSettings thì dùng newSettings, không thì dùng state hiện tại
        const settingsToSave = newSettings || settings;

        await set(settingsRef, settingsToSave);
        // Cập nhật state settings
        setSettings(settingsToSave);
      } catch (error) {
        console.error("Error saving settings:", error);
      }
    },
    [settings]
  );

  // Cập nhật các hàm xử lý sự kiện
  const handleIncludeContactInfoChange = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setIncludeContactInfo((prev) => {
        const newValue = !prev;
        // Cập nhật settings
        setSettings((prevSettings) => ({
          ...prevSettings,
          includeContactInfo: newValue,
        }));

        // Lưu settings
        const newSettings = {
          ...settings,
          includeContactInfo: newValue,
          contextFiles: contextFiles, // Thêm contextFiles vào settings
        };
        saveUserSettings(newSettings);

        return newValue;
      });
    },
    [settings, saveUserSettings]
  );

  const handleUseFanpageInfoChange = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setUseFanpageInfo((prev) => {
        const newValue = !prev;
        // Cập nhật settings
        setSettings((prevSettings) => ({
          ...prevSettings,
          useFanpageInfo: newValue,
        }));

        // Lưu settings
        const newSettings = {
          ...settings,
          useFanpageInfo: newValue,
          contextFiles: contextFiles, // Thêm contextFiles vào settings
        };
        saveUserSettings(newSettings);

        return newValue;
      });
    },
    [settings, saveUserSettings]
  );

  const handlePostStyleChange = (style) => {
    // Thay đổi hàm này để chỉ lưu một style duy nhất
    setPostStyles([style]);
    const newSettings = {
      ...settings,
      postStyles: [style],
      contextFiles: contextFiles, // Thêm contextFiles vào settings
    };
    saveUserSettings(newSettings);
  };

  // Hàm handleInputTypeChange đã bỏ vì mặc định inputType là "topic"

  const handleTopicCountChange = (newCount) => {
    setTopicCount(newCount);
    setIsTopicCountDropdownOpen(false);
    const newSettings = {
      ...settings,
      topicCount: newCount,
      contextFiles: contextFiles, // Thêm contextFiles vào settings
    };
    saveUserSettings(newSettings);
  };

  const handleAutoPostToggle = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const newValue = e.target.checked;
      setAutoPostEnabled(newValue);
      // Cập nhật settings
      setSettings((prevSettings) => ({
        ...prevSettings,
        autoPostEnabled: newValue,
      }));

      // Lưu settings
      const newSettings = {
        ...settings,
        autoPostEnabled: newValue,
        contextFiles: contextFiles, // Thêm contextFiles vào settings
      };
      saveUserSettings(newSettings);
    },
    [settings, saveUserSettings]
  );

  const handlePlatformTypeChange = (type) => {
    setPlatformType(type);
    const newSettings = {
      ...settings,
      platformType: type,
      contextFiles: contextFiles, // Thêm contextFiles vào settings
    };
    saveUserSettings(newSettings);
  };

  const toggleDropdown = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const handleFanpageSelect = useCallback((fanpage, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedFanpages([fanpage]);
    setIsDropdownOpen(false);
  }, []);

  // Toggle functions for new dropdowns with useCallback
  const toggleProjectTypeDropdown = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsProjectTypeDropdownOpen((prev) => !prev);
    // Đóng các dropdown khác để tránh xung đột
    setIsContentTypeDropdownOpen(false);
    setIsPublishingPlatformDropdownOpen(false);
  }, []);

  const toggleContentTypeDropdown = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsContentTypeDropdownOpen((prev) => !prev);
    // Đóng các dropdown khác để tránh xung đột
    setIsProjectTypeDropdownOpen(false);
    setIsPublishingPlatformDropdownOpen(false);
  }, []);

  const togglePublishingPlatformDropdown = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Không làm gì cả để ngăn không cho dropdown mở ra
    return;
  }, []);

  // Thêm function để điều hướng đến trang thiết lập fanpage
  const navigateToProfile = useCallback(() => {
    navigate("/profile");
  }, [navigate]);

  // Selection handlers with useCallback
  const handleProjectTypeSelect = useCallback((projectType, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedProjectType(projectType);
    setIsProjectTypeDropdownOpen(false);
  }, []);

  const handleContentTypeSelect = useCallback((contentType, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedContentType(contentType);
    setIsContentTypeDropdownOpen(false);
  }, []);

  const handlePublishingPlatformSelect = useCallback((platform, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Không thay đổi giá trị, luôn giữ mạng xã hội
    return;
  }, []);

  // Toggle accordion section with useCallback
  const toggleSection = useCallback((section, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Thêm CSS style cho transition mượt mà
  const dropdownStyle = useMemo(
    () => ({
      transition: "all 0.2s ease-in-out",
      transformOrigin: "top center",
    }),
    []
  );

  const transitionStyle = useMemo(
    () => ({
      transform: "none",
      willChange: "transform, opacity",
      backfaceVisibility: "hidden",
    }),
    []
  );

  // Memoize the AccordionSection component
  const AccordionSection = useMemo(() => {
    return ({ title, isExpanded, toggleExpand, children, icon }) => {
      return (
        <div className="mb-3 md:mb-4 border border-gray-200 rounded-md overflow-visible">
          <div
            className="flex justify-between items-center p-3 md:p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={(e) => toggleExpand(e)}
          >
            <div className="flex items-center">
              {icon && <span className="mr-2">{icon}</span>}
              <h3 className="font-medium text-gray-800">{title}</h3>
            </div>
            <div>
              {isExpanded ? (
                <FiChevronUp className="text-gray-500" />
              ) : (
                <FiChevronDown className="text-gray-500" />
              )}
            </div>
          </div>
          <div
            className={`transition-all duration-300 ease-in-out ${
              isExpanded
                ? "max-h-none opacity-100 overflow-visible"
                : "max-h-0 opacity-0 overflow-hidden"
            }`}
            style={{
              position: "relative",
              ...transitionStyle,
              ...(isExpanded && {
                maxHeight: "none",
                height: "auto",
              }),
            }}
          >
            {isExpanded && (
              <div className="p-3 md:p-4 border-t border-gray-200">
                {children}
              </div>
            )}
          </div>
        </div>
      );
    };
  }, [transitionStyle]);

  const handleGenerateFacebookTopics = async () => {
    // System is now free to use - no plan check needed

    // Kiểm tra xem người dùng đã cung cấp ít nhất một trong ba mục: Yêu cầu nội dung, URL tham khảo, hoặc Tài liệu tham khảo
    const hasRequirements = requirements && requirements.trim() !== "";
    const hasReferenceUrl = referenceUrl && referenceUrl.trim() !== "";
    const hasContextFiles = contextFiles && contextFiles.length > 0;

    if (!hasRequirements && !hasReferenceUrl && !hasContextFiles) {
      // Đảm bảo section projectInfo được mở ra để người dùng nhìn thấy các trường nhập liệu
      if (!expandedSections.projectInfo) {
        setExpandedSections((prev) => ({
          ...prev,
          projectInfo: true,
        }));
      }

      toast.error(
        "Vui lòng cung cấp ít nhất một trong ba mục: Yêu cầu nội dung hoặc Tài liệu tham khảo hoặc URL tham khảo",
        {
          position: "top-right",
          autoClose: 5000,
          icon: "ℹ️",
        }
      );
      return;
    }

    setIsGeneratingTopics(true);

    try {
      // Chuẩn bị requirements cuối cùng
      let finalRequirements = "";

      if (hasRequirements) {
        finalRequirements = requirements.trim();
      } else if (hasReferenceUrl) {
        finalRequirements = "Tạo chủ đề dựa trên URL tham khảo";
      } else if (hasContextFiles) {
        finalRequirements = "Tạo chủ đề dựa trên tài liệu tham khảo đã tải lên";
      }

      const result = await generateFacebookTopics(
        finalRequirements,
        topicCount,
        process.env.REACT_APP_GEMINI_API_KEY,
        referenceUrl,
        contextFiles, // Thêm contextFiles vào tham số
        domainConfig // Thêm domainConfig để tạo chủ đề chính xác hơn theo lĩnh vực
      );

      if (result.success) {
        setTopics(result.topics);
        toast.success("Danh sách chủ đề Facebook đã được tạo!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error(`Tạo danh sách chủ đề Facebook thất bại: ${result.error}`, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to generate Facebook topics:", error);
      toast.error("Tạo danh sách chủ đề Facebook thất bại!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsGeneratingTopics(false);
    }
  };

  const handleDeleteTopic = (topicId) => {
    setTopics((prevTopics) => deleteTopic(prevTopics, topicId));
  };

  const handleResetTopicsStatus = () => {
    setTopics((prevTopics) => resetTopicsStatus(prevTopics));

    toast.success("Đã reset trạng thái các chủ đề!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // Thêm hàm xử lý thay đổi ngày
  const handleDayChange = (day) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleSaveAutoPostSettings = async () => {
    // System is now free to use - no plan check needed

    if (!hasFanpages) {
      toast.error(
        "Bạn cần kết nối Fanpage trước khi lưu cài đặt đăng bài tự động!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
      return;
    }

    if (selectedFanpages.length === 0) {
      toast.error("Vui lòng chọn một Fanpage!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const selectedFanpage = selectedFanpages[0];
    // Kiểm tra access token: nếu đang ở chế độ edit thì sử dụng access token từ config hoặc từ fanpage được chọn
    if (!selectedFanpage.access_token && !config.id) {
      toast.error("Fanpage không có access token!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const userId = localStorage.getItem("userId");

    const settingsToSave = {
      platform, // Lưu nền tảng được chọn
      inputType: inputType,
      prompt: inputType === "products" ? "" : requirements,
      products: "",
      // Save both formats for backwards compatibility
      time: null, // Keep the first time for old format
      times: selectedTimes, // Save full array for new format
      autoPostEnabled: autoPostEnabled,
      userID: userId,
      postLength: postLength,
      postStyles:
        postStyles.length > 0 ? postStyles[0] : DEFAULT_POST_STYLES[0],
      topics: inputType === "topic" ? topics : {},
      includeContactInfo,
      useFanpageInfo,
      topicCount: settings.topicCount,
      selectedDays: selectedDays,
      allowNoImage: allowNoImage,
      platformType: platformType,
      // Lưu thông tin từ các trường mới
      projectType: selectedProjectType,
      contentType: selectedContentType,
      publishingPlatform: selectedPublishingPlatform,
      targetCustomer: targetCustomer,
      referenceUrl: referenceUrl, // Thêm URL tham khảo
      contextFiles: contextFiles, // Thêm tài liệu tham khảo
    };

    // Nếu không có access_token trong selectedFanpage, tìm từ danh sách fanpages đã load
    let accessToken = selectedFanpage.access_token || "";
    if (!accessToken && selectedFanpage.id) {
      // Tìm kiếm access_token từ list fanpages đã load
      const matchedFanpage = fanpages.find(
        (fp) => fp.id === selectedFanpage.id
      );
      if (matchedFanpage && matchedFanpage.access_token) {
        accessToken = matchedFanpage.access_token;
      }
    }

    // Lưu lại access token đã tìm được
    settingsToSave.accessToken = accessToken;
    settingsToSave.fanpageID = selectedFanpage.id || "";
    settingsToSave.fanpageName = selectedFanpage.name || "";
    settingsToSave.imageURLs = "";

    try {
      const result = await saveAutoPostSettings(config, settingsToSave, userId);

      if (result.success) {
        toast.success("Thiết lập đăng bài tự động đã được lưu!", {
          position: "top-right",
          autoClose: 3000,
        });

        // Reset màn hình ngay lập tức sau khi lưu thành công để người dùng tạo thiết lập mới
        resetConfig();
      } else {
        if (result.limitExceeded) {
          toast.error(`Không thể lưu thiết lập! ${result.error}`, {
            position: "top-right",
            autoClose: 5000,
          });
        } else {
          toast.error(`Lưu thiết lập thất bại: ${result.error}`, {
            position: "top-right",
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error(`Lưu thiết lập thất bại: ${error.message}`, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const resetConfig = () => {
    // Reset tất cả các trường về giá trị mặc định
    setRequirements("");
    setTargetCustomer("");
    setReferenceUrl(""); // Reset URL tham khảo

    // Reset các trường thông tin cơ bản
    setSelectedProjectType("Dự án khu đô thị (hỗn hợp)");
    setSelectedContentType("Giới thiệu, cập nhật thông tin dự án");
    setSelectedPublishingPlatform(
      "Đăng bài trên các nền tảng Mạng xã hội (Facebook, Zalo,…)"
    );

    // Set default post length
    setPostLength(DEFAULT_POST_LENGTH);

    // Reset các trường tùy chỉnh
    setPostStyles(DEFAULT_POST_STYLES);
    setAllowNoImage(false);
    setIncludeContactInfo(false);
    setUseFanpageInfo(false);
    setTopicCount(30);

    // Reset các trường lịch trình
    setSelectedFanpages([]);
    setSelectedTimes(["08:00"]);
    setTopics({});
    setPlatform("facebook"); // Reset nền tảng về mặc định
    setAutoPostEnabled(true);

    // Reset ngày đăng bài
    setSelectedDays({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    });

    // Reset tài liệu tham khảo
    setContextFiles([]);
    setIsProcessingContext(false);
    setIsDragging(false);
    setDragCounter(0);

    // Reset state của component
    setIsGeneratingTopics(false);
    setIsAnalyzingRequest(false);
    setTempTranscript("");

    // Reset accordion sections
    setExpandedSections({
      basicInfo: true,
      projectInfo: true,
      customization: true,
    });

    // Chuyển hướng về trang tạo mới
    navigate("/auto-post", { state: {} });
  };

  const handleDeleteConfig = () => {
    if (!config.id) {
      toast.error("Không có thiết lập để xóa!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Sử dụng confirmAlert để hiển thị popup xác nhận
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="bg-white rounded-lg shadow-xl overflow-hidden max-w-md w-full mx-auto">
            <div className="bg-red-50 p-4 flex items-center">
              <FiAlertTriangle className="text-red-500 text-2xl mr-3" />
              <h2 className="text-lg font-semibold text-gray-800">
                Xác nhận xóa thiết lập
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa thiết lập này? Hành động này không thể
                hoàn tác.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  Hủy
                </button>
                <button
                  onClick={async () => {
                    const userId = localStorage.getItem("userId");
                    const result = await deleteConfigFromFirebase(
                      userId,
                      config.id
                    );

                    if (result.success) {
                      toast.success("Đã xóa thiết lập thành công!", {
                        position: "top-right",
                        autoClose: 3000,
                      });
                      resetConfig();
                    } else {
                      toast.error(`Xóa thiết lập thất bại: ${result.error}`, {
                        position: "top-right",
                        autoClose: 3000,
                      });
                    }
                    onClose();
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Xóa thiết lập
                </button>
              </div>
            </div>
          </div>
        );
      },
    });
  };

  // Sử dụng hàm capitalizeFirstLetter từ file logic

  // Thêm useEffect để khởi tạo Web Speech API
  useEffect(() => {
    if ("webkitSpeechRecognition" in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "vi-VN";

      recognition.onresult = (event) => {
        const lastResult = event.results[event.results.length - 1];

        if (lastResult.isFinal) {
          let finalTranscript = capitalizeFirstLetter(
            lastResult[0].transcript.trim()
          );

          // Thêm dấu chấm nếu câu chưa có dấu câu kết thúc
          const lastChar = finalTranscript.slice(-1);
          if (![".", "!", "?"].includes(lastChar)) {
            finalTranscript += "."; // Đảm bảo thêm dấu chấm câu
          }

          setTempTranscript("");

          setRequirements((prevRequirements) => {
            const prevText = prevRequirements.trim();
            if (!prevText) {
              return finalTranscript;
            }
            // Kiểm tra xem câu trước có dấu câu kết thúc chưa
            const lastCharPrev = prevText.slice(-1);
            if ([".", "!", "?"].includes(lastCharPrev)) {
              return `${prevText} ${finalTranscript}`;
            }
            return `${prevText}. ${finalTranscript}`;
          });
        } else {
          const interimTranscript = capitalizeFirstLetter(
            lastResult[0].transcript.trim()
          );
          setTempTranscript(interimTranscript);
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        setTempTranscript("");
      };

      recognition.onend = () => {
        setIsRecording(false);
        setTempTranscript("");
      };

      setRecognition(recognition);
    }
  }, []);

  // Thêm hàm xử lý bắt đầu/dừng ghi âm
  const toggleRecording = () => {
    if (!recognition) {
      toast.error(
        "Trình duyệt của bạn không hỗ trợ nhập liệu bằng giọng nói!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
      return;
    }

    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
      setTempTranscript("");
    } else {
      recognition.start();
      setIsRecording(true);
    }
  };

  // Thêm hàm xử lý clear text
  const clearText = () => {
    setRequirements("");
    setTempTranscript("");
  };

  // Thêm hàm xử lý allowNoImage
  const handleAllowNoImageChange = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setAllowNoImage((prev) => {
        const newValue = !prev;
        // Cập nhật settings
        setSettings((prevSettings) => ({
          ...prevSettings,
          allowNoImage: newValue,
        }));

        // Lưu settings
        const newSettings = {
          ...settings,
          allowNoImage: newValue,
          contextFiles: contextFiles, // Thêm contextFiles vào settings
        };
        saveUserSettings(newSettings);

        return newValue;
      });
    },
    [settings, saveUserSettings]
  );

  // Cleanup effect khi component unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Xử lý khi người dùng click vào khu vực dropzone
  const handleDropzoneClick = (event) => {
    // Kiểm tra xem người dùng có đang click vào label hoặc input không
    const target = event.target;
    const isLabel = target.tagName === "LABEL" || target.closest("label");
    const isInput = target.tagName === "INPUT";

    // Chỉ kích hoạt click vào input file nếu người dùng không click vào label hoặc input
    if (!isLabel && !isInput && contextFileInputRef.current) {
      contextFileInputRef.current.click();
    }
  };

  // Xử lý khi người dùng bắt đầu kéo file vào khu vực dropzone
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter((prevCount) => prevCount + 1);
    setIsDragging(true);
  };

  // Xử lý khi người dùng kéo file qua khu vực dropzone
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Xử lý khi người dùng kéo file ra khỏi khu vực dropzone
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter((prevCount) => prevCount - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  // Xử lý khi người dùng thả file vào khu vực dropzone
  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter(0);
    setIsDragging(false);

    // Lấy danh sách các file được thả
    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) return;

    // Kiểm tra số lượng file hiện tại và file mới
    if (contextFiles.length + files.length > 5) {
      toast.error("Chỉ cho phép tối đa 5 file tài liệu tham khảo", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    // Tính tổng kích thước hiện tại của các file đã có
    let currentTotalSize = 0;
    contextFiles.forEach((file) => {
      if (file.file && file.file.size) {
        currentTotalSize += file.file.size;
      }
    });

    // Tính tổng kích thước của các file mới
    let newFilesTotalSize = 0;
    files.forEach((file) => {
      newFilesTotalSize += file.size;
    });

    // Kiểm tra nếu tổng kích thước vượt quá giới hạn 10MB
    const maxTotalSize = 10 * 1024 * 1024; // 10MB
    if (currentTotalSize + newFilesTotalSize > maxTotalSize) {
      toast.error(
        `Tổng kích thước file (${(
          (currentTotalSize + newFilesTotalSize) /
          (1024 * 1024)
        ).toFixed(2)}MB) vượt quá giới hạn 10MB`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    // Sử dụng lại hàm xử lý file đã có
    await handleFiles(files);
  };

  // Xử lý khi người dùng chọn file từ input
  const handleContextFileChange = async (e) => {
    const files = Array.from(e.target.files);

    // Kiểm tra số lượng file hiện tại và file mới
    if (contextFiles.length + files.length > 5) {
      toast.error("Chỉ cho phép tối đa 5 file tài liệu tham khảo", {
        position: "top-right",
        autoClose: 5000,
      });
      // Reset input file để có thể chọn lại cùng một file
      e.target.value = null;
      return;
    }

    // Tính tổng kích thước hiện tại của các file đã có
    let currentTotalSize = 0;
    contextFiles.forEach((file) => {
      if (file.file && file.file.size) {
        currentTotalSize += file.file.size;
      }
    });

    // Tính tổng kích thước của các file mới
    let newFilesTotalSize = 0;
    files.forEach((file) => {
      newFilesTotalSize += file.size;
    });

    // Kiểm tra nếu tổng kích thước vượt quá giới hạn 10MB
    const maxTotalSize = 10 * 1024 * 1024; // 10MB
    if (currentTotalSize + newFilesTotalSize > maxTotalSize) {
      toast.error(
        `Tổng kích thước file (${(
          (currentTotalSize + newFilesTotalSize) /
          (1024 * 1024)
        ).toFixed(2)}MB) vượt quá giới hạn 10MB`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      // Reset input file để có thể chọn lại cùng một file
      e.target.value = null;
      return;
    }

    await handleFiles(files);
    // Reset input file để có thể chọn lại cùng một file
    e.target.value = null;
  };

  // Hàm xử lý file
  const handleFiles = async (files) => {
    setIsProcessingContext(true);

    try {
      const processedFiles = [];

      for (const file of files) {
        // Kiểm tra kích thước file (tối đa 2MB)
        if (file.size > 2 * 1024 * 1024) {
          toast.error(`File "${file.name}" vượt quá giới hạn 2MB`, {
            position: "top-right",
            autoClose: 5000,
          });
          continue;
        }

        // Kiểm tra loại file
        const allowedTypes = [
          "application/pdf",
          "text/plain",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ];

        if (!allowedTypes.includes(file.type)) {
          toast.error(
            `File "${file.name}" không được hỗ trợ. Chỉ chấp nhận PDF, TXT, JPG, PNG`,
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
          continue;
        }

        let content = "";
        let type = "";

        if (file.type === "application/pdf") {
          // Xử lý file PDF - tạm thời placeholder
          type = "pdf";
          content = `[PDF File: ${file.name}] - Nội dung PDF sẽ được xử lý để tạo chủ đề phù hợp`;
        } else if (file.type === "text/plain") {
          // Xử lý file text
          type = "text";
          const text = await file.text();
          // Giới hạn độ dài nội dung để tránh vượt quá giới hạn API
          content =
            text.length > 10000 ? text.substring(0, 10000) + "..." : text;
        } else if (file.type.startsWith("image/")) {
          // Xử lý file ảnh
          type = "image";
          content = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(file);
          });
        }

        processedFiles.push({
          name: file.name,
          type: type,
          content: content,
          file: file,
        });
      }

      // Thêm các file đã xử lý vào danh sách
      setContextFiles((prevFiles) => [...prevFiles, ...processedFiles]);

      if (processedFiles.length > 0) {
        toast.success(
          `Đã tải lên ${processedFiles.length} file tài liệu tham khảo`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      }
    } catch (error) {
      console.error("Error processing files:", error);
      toast.error("Có lỗi khi xử lý file. Vui lòng thử lại.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessingContext(false);
    }
  };

  // Hàm xóa file tài liệu tham khảo
  const removeContextFile = useCallback(
    (index) => {
      setContextFiles(contextFiles.filter((_, i) => i !== index));
    },
    [contextFiles]
  );

  // Hàm handlePlatformChange đã bỏ vì mặc định platform là "facebook"

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-2 md:px-4 sm:px-3 md:sm:px-6 lg:px-4 md:lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          TẠO & ĐĂNG BÀI VIẾT TỰ ĐỘNG
        </h1>

        <div className="flex flex-col md:flex-row gap-3 md:gap-6">
          {/* Phần bên trái - Cài đặt cơ bản */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-3 md:p-5 mb-4 md:mb-6">
              {/* <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Thiết lập tự động hóa
                </h2>
              </div> */}

              {/* Đã xóa phần chọn nền tảng đăng bài, mặc định là Facebook */}

              {/* Đã xóa phần chọn inputType, mặc định là tạo từ chủ đề */}

              {/* Thông tin cơ bản */}
              <AccordionSection
                title="Thông tin cơ bản"
                isExpanded={expandedSections.basicInfo}
                toggleExpand={(e) => toggleSection("basicInfo", e)}
                icon={<FiInfo />}
              >
                <BasicInfoSection
                  selectedProjectType={selectedProjectType}
                  setSelectedProjectType={setSelectedProjectType}
                  selectedContentType={selectedContentType}
                  setSelectedContentType={setSelectedContentType}
                  selectedPublishingPlatform={selectedPublishingPlatform}
                  setSelectedPublishingPlatform={setSelectedPublishingPlatform}
                  isProjectTypeDropdownOpen={isProjectTypeDropdownOpen}
                  setIsProjectTypeDropdownOpen={setIsProjectTypeDropdownOpen}
                  isContentTypeDropdownOpen={isContentTypeDropdownOpen}
                  setIsContentTypeDropdownOpen={setIsContentTypeDropdownOpen}
                  isPublishingPlatformDropdownOpen={
                    isPublishingPlatformDropdownOpen
                  }
                  setIsPublishingPlatformDropdownOpen={
                    setIsPublishingPlatformDropdownOpen
                  }
                  projectTypeDropdownRef={projectTypeDropdownRef}
                  contentTypeDropdownRef={contentTypeDropdownRef}
                  publishingPlatformDropdownRef={publishingPlatformDropdownRef}
                  toggleProjectTypeDropdown={toggleProjectTypeDropdown}
                  toggleContentTypeDropdown={toggleContentTypeDropdown}
                  togglePublishingPlatformDropdown={
                    togglePublishingPlatformDropdown
                  }
                  handleProjectTypeSelect={handleProjectTypeSelect}
                  handleContentTypeSelect={handleContentTypeSelect}
                  handlePublishingPlatformSelect={
                    handlePublishingPlatformSelect
                  }
                  isAutoPostGenerator={true} // Thêm prop này để chỉ định đây là AutoPostGenerator
                  onDomainChange={handleDomainChange}
                  domainConfig={domainConfig} // Truyền domainConfig để đảm bảo consistency
                  refreshKey={refreshKey} // ✅ Add refreshKey để đảm bảo consistency với NormalPostGenerator
                />
              </AccordionSection>

              {/* Thông tin dự án */}
              <AccordionSection
                key={`project-info-${
                  domainConfig?.id || "default"
                }-${refreshKey}`}
                title={
                  domainConfig?.labels?.projectInfoLabel || "Thông tin dự án"
                }
                isExpanded={expandedSections.projectInfo}
                toggleExpand={() => toggleSection("projectInfo")}
              >
                <ProjectInfoSection
                  requirements={requirements}
                  setRequirements={setRequirements}
                  targetCustomer={targetCustomer}
                  setTargetCustomer={setTargetCustomer}
                  referenceUrl={referenceUrl}
                  setReferenceUrl={setReferenceUrl}
                  isSuggestTopicsShown={false}
                  domainConfig={domainConfig || null}
                />

                {/* File context */}
                <div className="mb-4">
                  <div className="mb-1">
                    <label className="block text-sm font-medium text-gray-700 mt-4">
                      Tài liệu tham khảo
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">
                    Tối đa 5 file, mỗi file không quá 2MB, tổng dung lượng không
                    quá 10MB
                  </p>
                  <div
                    className={`mt-1 flex justify-center px-3 md:px-6 pt-3 md:pt-5 pb-3 md:pb-6 border-2 ${
                      isDragging
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 hover:bg-gray-50"
                    } border-dashed rounded-md cursor-pointer transition-colors duration-150`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleDropzoneClick}
                  >
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="context-file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                        >
                          <span>Tải lên tài liệu</span>
                          <input
                            id="context-file-upload"
                            name="context-file-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            ref={contextFileInputRef}
                            onChange={handleContextFileChange}
                            accept=".pdf,.txt,.jpg,.jpeg,.png"
                          />
                        </label>
                        <p className="pl-1">hoặc kéo và thả</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PDF, TXT, JPG, PNG
                      </p>
                    </div>
                  </div>

                  {/* Hiển thị danh sách file đã tải lên */}
                  {contextFiles.length > 0 && (
                    <div className="mt-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        Tài liệu đã tải lên:
                      </h4>
                      <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                        {contextFiles.map((file, index) => (
                          <li
                            key={index}
                            className="pl-2 md:pl-3 pr-3 md:pr-4 py-2 md:py-3 flex items-center justify-between text-sm"
                          >
                            <div className="w-0 flex-1 flex items-center">
                              {file.type === "image" ? (
                                <svg
                                  className="flex-shrink-0 h-5 w-5 text-gray-400"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="flex-shrink-0 h-5 w-5 text-gray-400"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                              <span className="ml-2 flex-1 w-0 truncate">
                                {file.name}
                              </span>
                            </div>
                            <div className="ml-4 flex-shrink-0">
                              <button
                                onClick={() => removeContextFile(index)}
                                className="font-medium text-red-600 hover:text-red-500"
                              >
                                Xóa
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {isProcessingContext && (
                    <div className="mt-2 text-sm text-gray-500">
                      <div className="flex items-center">
                        <FiRefreshCw className="animate-spin mr-1" />
                        <span>Đang xử lý tài liệu...</span>
                      </div>
                    </div>
                  )}
                </div>
              </AccordionSection>

              <AccordionSection
                title="Tùy chỉnh & Phương tiện"
                isExpanded={expandedSections.customization}
                toggleExpand={() => toggleSection("customization")}
              >
                {/* Thêm chế độ bài viết chỉ văn bản không hình ảnh */}
                <div className="mb-3 md:mb-4 flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-md">
                  <label className="text-gray-700 font-medium">
                    Tạo bài viết không kèm ảnh
                  </label>
                  <div className="relative inline-block w-12 align-middle select-none">
                    <input
                      type="checkbox"
                      name="allowNoImage"
                      id="allowNoImage"
                      checked={allowNoImage}
                      onChange={(e) => handleAllowNoImageChange(e)}
                      className="hidden"
                    />
                    <label
                      htmlFor="allowNoImage"
                      className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                        ${allowNoImage ? "bg-blue-600" : "bg-gray-300"}`}
                    >
                      <span
                        className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                          ${
                            allowNoImage
                              ? "translate-x-6 bg-white"
                              : "translate-x-0 bg-white"
                          }`}
                      />
                    </label>
                  </div>
                </div>

                <div className="mt-3 mb-2">
                  <label className="block text-gray-700 font-medium mb-2">
                    Số lượng chủ đề
                  </label>
                  <div className="grid grid-cols-12 gap-2 md:gap-3">
                    <div
                      className="relative col-span-8"
                      ref={topicCountDropdownRef}
                    >
                      <button
                        onClick={() =>
                          setIsTopicCountDropdownOpen(!isTopicCountDropdownOpen)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center bg-white"
                      >
                        <span>{topicCount} chủ đề</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${
                            isTopicCountDropdownOpen ? "rotate-180" : "rotate-0"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          ></path>
                        </svg>
                      </button>
                      {isTopicCountDropdownOpen && (
                        <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
                          <div className="flex flex-col">
                            {[10, 20, 30, 40, 50, 100].map((count) => (
                              <button
                                key={count}
                                onClick={() => handleTopicCountChange(count)}
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                              >
                                {count} chủ đề
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleGenerateFacebookTopics}
                      className={`col-span-4 flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base font-medium ${
                        isGeneratingTopics
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={isGeneratingTopics}
                    >
                      {isGeneratingTopics && (
                        <FiRefreshCw
                          className={`mr-2 h-5 w-5 ${
                            isGeneratingTopics ? "animate-spin" : ""
                          }`}
                        />
                      )}
                      {isGeneratingTopics ? "Đang tạo..." : "Tạo chủ đề"}
                    </button>
                  </div>
                </div>

                {Object.keys(topics).length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-gray-700 font-medium mb-2">
                      Danh sách chủ đề:
                    </h3>
                    <ul className="list-disc list-inside">
                      {Object.values(topics).map((topic, index) => (
                        <li
                          key={topic.id}
                          className="flex justify-between items-center py-1"
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center">
                              <button
                                onClick={() => handleDeleteTopic(topic.id)}
                                className="mr-2 text-red-500 hover:text-red-700"
                              >
                                <FiTrash2 size={18} />
                              </button>
                              <span>
                                {index + 1}. {topic.title}
                              </span>
                            </div>
                            <div className="ml-2">
                              {topic.postStatus === "posted" && (
                                <FiCheck className="text-green-500" size={16} />
                              )}
                              {topic.postStatus === "failed" && (
                                <FiX className="text-red-500" size={16} />
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {config.id && (
                      <button
                        onClick={handleResetTopicsStatus}
                        className="mt-4 flex items-center justify-center w-full px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                      >
                        <FiRefreshCw className="mr-2" />
                        Reset trạng thái đăng bài
                      </button>
                    )}
                  </div>
                )}

                <div className="mb-4 md:mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Độ dài bài viết
                  </label>
                  <div className="flex w-full rounded-md shadow-sm">
                    {POST_LENGTHS.map(({ value, label }, index) => (
                      <button
                        key={value}
                        type="button"
                        className={`flex-1 py-2 px-2 text-center text-sm font-medium
                          ${index === 0 ? "rounded-l-md" : ""}
                          ${
                            index === POST_LENGTHS.length - 1
                              ? "rounded-r-md"
                              : ""
                          }
                          ${
                            postLength === value
                              ? "bg-blue-600 text-white z-10"
                              : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPostLength(value);
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4 md:mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Phong cách bài viết
                  </label>
                  <div className="flex flex-wrap gap-1 md:gap-2">
                    {POST_STYLES.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        className={`py-2 px-4 rounded-full text-sm font-medium
                          ${
                            postStyles.includes(value)
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handlePostStyleChange(value);
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-6">
                  <div className="flex flex-row gap-3 md:gap-4">
                    <div className="flex-1 p-2 md:p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <div className="flex items-center justify-between">
                        <label className="text-gray-700 font-medium">
                          Thêm thông tin liên hệ
                        </label>
                        <div className="relative inline-block align-middle select-none">
                          <input
                            type="checkbox"
                            name="contactInfo"
                            id="contactInfo"
                            checked={includeContactInfo}
                            onChange={(e) => handleIncludeContactInfoChange(e)}
                            className="hidden"
                          />
                          <label
                            htmlFor="contactInfo"
                            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                            style={{
                              backgroundColor: includeContactInfo
                                ? "#3b82f6"
                                : "#e5e7eb",
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                                includeContactInfo
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionSection>
            </div>
          </div>

          {/* Phần bên phải - Cài đặt lịch trình */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-3 md:p-5 mb-4 md:mb-6">
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Cài đặt lịch trình
                </h2>
              </div>

              {/* Hiển thị thông tin giới hạn giờ đăng bài */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      Giới hạn giờ đăng bài tự động
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Đã sử dụng: {hourLimitInfo.totalHours}/
                      {hourLimitInfo.maxHours} giờ
                      {autoPostEnabled && selectedTimes.length > 0 && (
                        <span>
                          {" "}
                          (Thiết lập này: +{selectedTimes.length} giờ)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-700">
                      {hourLimitInfo.remaining} giờ
                    </p>
                    <p className="text-xs text-blue-600">còn lại</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      hourLimitInfo.totalHours >= hourLimitInfo.maxHours
                        ? "bg-red-500"
                        : hourLimitInfo.totalHours >= 4
                        ? "bg-yellow-500"
                        : "bg-blue-500"
                    }`}
                    style={{
                      width: `${
                        (hourLimitInfo.totalHours / hourLimitInfo.maxHours) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>

              <div className="mb-4">
                <div className="flex flex-col md:flex-row gap-3 md:gap-4">
                  <div className="w-full md:w-1/3 p-2 md:p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                    <label className="block text-gray-700 font-medium mb-2">
                      Giờ đăng bài
                    </label>
                    <div className="space-y-2">
                      {selectedTimes.map((time, index) => (
                        <div key={index} className="flex items-center gap-2">
                          {/* Thay thế input type="time" bằng CustomTimePicker */}
                          <CustomTimePicker
                            value={time}
                            onChange={(newTime) => {
                              const newTimes = [...selectedTimes];
                              newTimes[index] = newTime;
                              setSelectedTimes(newTimes);
                            }}
                          />
                          {/* <input
                            type="time"
                            value={time}
                            onChange={(e) => {
                              const newTimes = [...selectedTimes];
                              newTimes[index] = e.target.value;
                              setSelectedTimes(newTimes);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                            style={{
                              backgroundImage: "none",
                            }}
                            data-format="24h"
                            step="300" // Thêm step 5 phút (300 giây)
                          /> */}
                          <button
                            type="button"
                            onClick={() => {
                              const newTimes = selectedTimes.filter(
                                (_, i) => i !== index
                              );
                              setSelectedTimes(newTimes);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedTimes([...selectedTimes, "00:00"])
                        }
                        className="w-full px-4 py-2 text-blue-600 border border-blue-500 rounded-md hover:bg-blue-50"
                      >
                        Thêm giờ đăng
                      </button>
                    </div>
                  </div>

                  <div className="w-full md:w-2/3 p-2 md:p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                    <label className="block text-gray-700 font-medium mb-2">
                      Ngày đăng bài
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(daysInVietnamese).map(([day, label]) => (
                        <label
                          key={day}
                          className={`inline-flex items-center justify-center px-3 py-2 rounded-md cursor-pointer select-none transition-colors duration-200
                            ${
                              selectedDays[day]
                                ? "bg-blue-500 text-white"
                                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={selectedDays[day]}
                            onChange={() => handleDayChange(day)}
                          />
                          <span className="text-sm font-medium">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Hiển thị phần chọn Fanpage */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Chọn Fanpage để đăng bài
                </label>
                {hasFanpages ? (
                  <div ref={dropdownRef} className="relative">
                    <button
                      onClick={toggleDropdown}
                      className="w-full px-4 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {selectedFanpages.length > 0
                        ? `${selectedFanpages[0].name}`
                        : "Chọn Fanpage"}
                    </button>
                    {isDropdownOpen && (
                      <div
                        className="absolute w-full z-10 bg-white border border-gray-200 rounded-md shadow-lg mt-1"
                        style={{ ...dropdownStyle }}
                      >
                        {fanpages.map((fanpage) => (
                          <div
                            key={fanpage.id}
                            onClick={() => handleFanpageSelect(fanpage)}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                              selectedFanpages.find(
                                (fp) => fp.id === fanpage.id
                              )
                                ? "bg-blue-50"
                                : ""
                            }`}
                          >
                            <span className="text-left truncate">
                              {fanpage.name}
                            </span>
                            {selectedFanpages.find(
                              (fp) => fp.id === fanpage.id
                            ) && <span className="text-blue-600">✓</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-4">
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-yellow-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            Chưa có Fanpage kết nối
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>
                              Bạn cần kết nối Fanpage Facebook để sử dụng tính
                              năng đăng bài tự động.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={navigateToProfile}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm flex items-center justify-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Đến trang Thiết lập Fanpage
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4 md:mb-6">
                <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-md">
                  <label className="block text-gray-700 font-medium">
                    Tự động viết và đăng bài
                  </label>
                  <div className="relative inline-block align-middle select-none">
                    <input
                      type="checkbox"
                      name="autoPost"
                      id="autoPost"
                      checked={autoPostEnabled}
                      onChange={(e) => handleAutoPostToggle(e)}
                      className="hidden"
                    />
                    <label
                      htmlFor="autoPost"
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                      style={{
                        backgroundColor: autoPostEnabled
                          ? "#3b82f6"
                          : "#e5e7eb",
                      }}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                          autoPostEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </label>
                  </div>
                </div>
                {autoPostEnabled ? (
                  <p className="mt-2 text-sm text-gray-600">
                    Hệ thống sẽ tự động viết và đăng bài theo thời gian đã cài
                    đặt
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    Hiện tại tính năng tự động viết và đăng bài tự động đã tắt.
                    Vui lòng bật để sử dụng
                  </p>
                )}
              </div>

              <div className="mt-4 md:mt-6">
                <button
                  onClick={handleSaveAutoPostSettings}
                  className="flex items-center justify-center w-full bg-green-600 text-white py-2 px-3 md:px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm sm:text-base font-medium"
                >
                  <FiSave className="mr-2 h-5 w-5" />
                  Lưu thiết lập đăng bài tự động
                </button>
                <div className="flex flex-col sm:flex-row gap-1 md:gap-2 mt-2">
                  {config.id && (
                    <button
                      onClick={handleDeleteConfig}
                      className="flex items-center justify-center w-full bg-red-600 text-white py-2 px-3 md:px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      <FiTrash2 className="mr-2" />
                      Xóa thiết lập
                    </button>
                  )}
                  <button
                    onClick={resetConfig}
                    className="flex items-center justify-center w-full bg-gray-600 text-white py-2 px-3 md:px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Tạo thiết lập mới
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={6000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        pauseOnHover={true}
        draggable={true}
        theme="light"
        style={{ zIndex: 9999 }}
        toastClassName="toast-custom"
        bodyClassName="toast-body-custom"
        progressClassName="toast-progress-custom"
      />
    </div>
  );
};

export default AutoPostGenerator;
